import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
    Users, UserPlus, RefreshCcw, Info, Shield, Trash2, Copy, Sparkles,
    Lock, Eye, EyeOff, LogOut, Wallet,
} from "lucide-react";
import HouseholdNav from "@/components/HouseholdNav";

/**
 * Settings · Household · VAULT Together
 * MY MONEY · YOUR MONEY · OUR MONEY — one organised view, with privacy.
 *
 * Layout:
 *   1. Household card (workspace type toggle + seat count)
 *   2. Privacy preset chooser (Fully Shared · Balanced · Private First)
 *   3. Privacy Review — what each new member will see, per module
 *   4. Contribution Totals — summary-only totals across the household
 *   5. View switcher (Together / Mine / Partner / Joint)
 *   6. Household VAULT Brief (Private items excluded)
 *   7. Member list w/ role + remove + leave + departure preview
 *   8. Invite form + shareable link
 *   9. Family Demo seed + purge (labelled DEMO DATA ONLY)
 */

const VIEWS = [
    { key: "together",  label: "Together" },
    { key: "mine",      label: "Mine" },
    { key: "partner",   label: "Partner" },
    { key: "joint",     label: "Joint" },
];

const ROLES = ["Owner", "Co-Admin", "Adult", "Viewer"];
const RELATIONSHIPS = ["Spouse", "Partner", "Parent", "Adult Child", "Sibling", "Family Member", "Other"];

const PRESET_META = {
    "Fully Shared Couple": { icon: Eye,   tone: "emerald",
        blurb: "Everything is Joint · Shared Full by default. Best when both partners want full transparency." },
    "Balanced Privacy":    { icon: EyeOff, tone: "amber",
        blurb: "Personal ownership · Shared Summary by default. Amounts contribute to household totals; individual transactions stay yours." },
    "Private First":       { icon: Lock,   tone: "stone",
        blurb: "Personal · Private by default. Nothing appears in household views unless you explicitly share it." },
};

const TONE_CARD = {
    emerald: "border-emerald-200 bg-emerald-50/40",
    amber:   "border-amber-200 bg-amber-50/40",
    stone:   "border-stone-300 bg-stone-100/40",
};

const REVIEW_LABEL = {
    "Private":          { icon: Lock,   tone: "text-stone-600",  hint: "Never appears in household views." },
    "Shared Summary":   { icon: EyeOff, tone: "text-amber-700",  hint: "Amounts contribute to household totals only." },
    "Shared Full":      { icon: Eye,    tone: "text-emerald-700",hint: "Fully visible to the household." },
    "Selected Members": { icon: Users,  tone: "text-blue-700",   hint: "Only chosen members can see this." },
    "Joint":            { icon: Users,  tone: "text-blue-700",   hint: "Jointly-held by the household." },
    "Household":        { icon: Users,  tone: "text-blue-700",   hint: "Belongs to the household as a whole." },
    "Unclassified":     { icon: Info,   tone: "text-stone-500",  hint: "No privacy label yet — set one before inviting a member." },
};


function fmtINR(v) {
    const n = Number(v || 0);
    if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
    if (n >= 1000)  return `₹${(n / 1000).toFixed(1)}K`;
    return `₹${n.toFixed(0)}`;
}


export default function VaultTogether() {
    const [state, setState] = useState({ household: null, members: [], meta: {} });
    const [view, setView] = useState("together");
    const [together, setTogether] = useState(null);
    const [brief, setBrief] = useState(null);
    const [inv, setInv] = useState({ name: "", email: "", role: "Co-Admin", relationship: "Spouse" });
    const [lastInvite, setLastInvite] = useState(null);
    const [preset, setPreset] = useState({ presets: [], current: "Balanced Privacy", defaults: null });
    const [review, setReview] = useState(null);
    const [totals, setTotals] = useState(null);
    const [departure, setDeparture] = useState(null);
    const [confirmInvite, setConfirmInvite] = useState(false);
    const [busySeed, setBusySeed] = useState(false);

    const load = async () => {
        const [h, b, p, r, t] = await Promise.all([
            api.get("/household").catch(() => null),
            api.get("/household/brief").catch(() => null),
            api.get("/household/privacy/preset").catch(() => null),
            api.get("/household/privacy/review").catch(() => null),
            api.get("/household/totals/contribution").catch(() => null),
        ]);
        setState(h?.data || { household: null, members: [], meta: {} });
        setBrief(b?.data || null);
        setPreset(p?.data || { presets: [], current: "Balanced Privacy", defaults: null });
        setReview(r?.data?.review || null);
        setTotals(t?.data?.totals || null);
    };
    const loadView = async (v) => {
        setView(v);
        const r = await api.get("/household/together", { params: { view: v } }).catch(() => null);
        setTogether(r?.data || null);
    };
    useEffect(() => { load().then(() => loadView("together")); }, []);

    const upgrade = async (workspace_type) => {
        try { await api.post("/household/upgrade", { workspace_type }); toast.success(`Upgraded to ${workspace_type}`); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't upgrade"); }
    };

    const applyPreset = async (p) => {
        try {
            await api.post("/household/privacy/preset", { preset: p });
            toast.success(`Preset applied · ${p}`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't apply preset"); }
    };

    const openConfirmInvite = () => {
        if (!inv.name || !inv.email) return toast.error("Name and email required");
        setConfirmInvite(true);
    };

    const sendInvite = async () => {
        try {
            const r = await api.post("/household/members/invite", inv);
            setLastInvite(r.data.invite);
            toast.success("Invite sent · they can accept with their own login");
            setConfirmInvite(false);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't invite"); }
    };

    const removeMember = async (m) => {
        if (!window.confirm(`Remove ${m.name || m.email} from the household?`)) return;
        try { await api.delete(`/household/members/${m.id}`); toast.success("Removed"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't remove"); }
    };

    const previewDeparture = async (m) => {
        try {
            const r = await api.get(`/household/members/${m.id}/departure-preview`);
            setDeparture(r.data);
        } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't preview"); }
    };

    const changeRole = async (m, role) => {
        try { await api.patch(`/household/members/${m.id}/role`, { role }); toast.success("Role updated"); load(); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't update role"); }
    };

    const seedDemo = async () => {
        if (!window.confirm("Seed a synthetic Alex + Sam household (DEMO DATA ONLY)?\n\nThis creates ~10 sample records tagged _demo=True and does not touch your real records.")) return;
        setBusySeed(true);
        try { const r = await api.post("/household/seed-demo"); toast.success(`Seeded ${r.data.records_seeded} demo records`); load(); loadView("together"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't seed demo"); }
        finally { setBusySeed(false); }
    };

    const purgeDemo = async () => {
        if (!window.confirm("Delete all demo rows (DEMO DATA ONLY) in this workspace?")) return;
        setBusySeed(true);
        try { const r = await api.delete("/household/seed-demo"); toast.success(`Removed ${r.data.removed} demo rows`); load(); loadView("together"); }
        catch (e) { toast.error(e?.response?.data?.detail || "Couldn't purge"); }
        finally { setBusySeed(false); }
    };

    const h = state.household;

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="vault-together">
            <HouseholdNav />
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <Users size={12} /> VAULT · Together
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        Share what matters. Keep what&rsquo;s personal, personal.
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        MY MONEY · YOUR MONEY · OUR MONEY — every household member keeps their own login and
                        their own privacy. Only records you share are visible together.
                    </p>
                </div>
                <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                        data-testid="together-refresh">
                    <RefreshCcw size={12} /> Refresh
                </button>
            </div>

            {/* 1. Household header */}
            {h && (
                <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6" data-testid="household-card">
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div>
                            <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                                {h.workspace_type} · {h.country} · {h.primary_currency}
                            </div>
                            <div className="text-2xl font-display text-stone-900 mt-1">{h.household_name}</div>
                            <div className="text-[11.5px] text-stone-500 mt-1">
                                Seats {state.members.length}/{h.seat_limits?.max_members || 1} ·
                                Timezone {h.timezone}
                            </div>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                            {["Personal", "Couple", "Family"].map(wt => (
                                <button key={wt} onClick={() => upgrade(wt)} disabled={h.workspace_type === wt}
                                        className={`text-[12.5px] font-semibold px-3 py-1.5 rounded-full border ${h.workspace_type === wt ? "bg-stone-900 text-white border-stone-900" : "border-stone-300 text-stone-700 hover:border-stone-500"}`}
                                        data-testid={`upgrade-${wt.toLowerCase()}`}>
                                    {wt}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* 2. Privacy preset chooser */}
            {(preset.presets || []).length > 0 && (
                <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6" data-testid="privacy-preset-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Shield size={13} className="text-stone-500" />
                        <h2 className="text-lg font-display text-stone-900">Privacy preset</h2>
                        <span className="ml-1 text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Applies to newly-added records</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        {preset.presets.map(p => {
                            const meta = PRESET_META[p] || {};
                            const Icon = meta.icon || Shield;
                            const isCurrent = preset.current === p;
                            return (
                                <button key={p} onClick={() => applyPreset(p)}
                                        className={`text-left rounded-xl border p-4 transition-all ${TONE_CARD[meta.tone] || "border-stone-200 bg-white"} ${isCurrent ? "ring-2 ring-stone-900" : "hover:border-stone-500"}`}
                                        data-testid={`privacy-preset-${p.toLowerCase().replace(/\s+/g, "-")}`}>
                                    <div className="flex items-center gap-2 mb-1.5">
                                        <Icon size={13} className="text-stone-700" />
                                        <div className="text-[13px] font-semibold text-stone-900">{p}</div>
                                        {isCurrent && <span className="ml-auto text-[10px] font-bold uppercase tracking-widest text-emerald-700">Active</span>}
                                    </div>
                                    <div className="text-[11.5px] text-stone-600 leading-relaxed">{meta.blurb}</div>
                                </button>
                            );
                        })}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-3">
                        Presets set defaults for <strong>new</strong> records. Existing records keep their labels
                        until you change them explicitly in the Privacy Review below.
                    </div>
                </div>
            )}

            {/* 3. Privacy Review */}
            {review && (
                <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6" data-testid="privacy-review-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Info size={13} className="text-stone-500" />
                        <h2 className="text-lg font-display text-stone-900">Privacy review</h2>
                        <span className="ml-1 text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Before adding a member</span>
                    </div>
                    <p className="text-[12px] text-stone-500 mb-3">
                        Review what a new household member will be able to see. Titles are never shown here — just
                        counts per module. Records marked <strong>Private</strong> or missing a label are never
                        included in household views.
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(review).map(([mod, buckets]) => (
                            <div key={mod} className="border border-stone-200 rounded-xl p-3">
                                <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold mb-1.5">{mod}</div>
                                <div className="grid grid-cols-2 gap-1.5">
                                    {Object.entries(buckets).map(([bkt, n]) => {
                                        const meta = REVIEW_LABEL[bkt];
                                        if (!meta) return null;
                                        const Icon = meta.icon;
                                        return (
                                            <div key={bkt} className="flex items-center gap-1.5 text-[11.5px]" data-testid={`privacy-review-${mod}-${bkt.toLowerCase().replace(/\s+/g, "-")}`}>
                                                <Icon size={10} className={meta.tone} />
                                                <span className="text-stone-600 truncate">{bkt}</span>
                                                <span className="tabular-nums font-semibold text-stone-900 ml-auto">{n}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* 4. Contribution Totals */}
            {totals && (
                <div className="bg-white border border-stone-200 rounded-2xl p-5 mb-6" data-testid="contribution-totals-card">
                    <div className="flex items-center gap-2 mb-3">
                        <Wallet size={13} className="text-stone-500" />
                        <h2 className="text-lg font-display text-stone-900">Household totals</h2>
                        <span className="ml-1 text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Summary only · never transactions</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {["assets", "investments", "liabilities", "household_net_worth"].map(k => (
                            <div key={k} className="border border-stone-200 rounded-xl p-3 bg-stone-50/50" data-testid={`totals-${k}`}>
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{k.replace(/_/g, " ")}</div>
                                <div className="text-2xl font-display tabular-nums text-stone-900 mt-1">{fmtINR(totals[k])}</div>
                            </div>
                        ))}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-3">
                        Values contribute to household totals only. Underlying transactions, documents, account
                        numbers and private notes are never exposed here.
                    </div>
                </div>
            )}

            {/* 5. View switcher */}
            <div className="flex flex-wrap gap-1 border border-stone-200 rounded-full p-1 bg-white w-fit mb-4"
                 role="tablist" data-testid="together-view-switcher">
                {VIEWS.map(v => (
                    <button key={v.key} onClick={() => loadView(v.key)}
                            className={`px-4 py-1.5 text-[12.5px] font-semibold rounded-full transition-colors ${view === v.key ? "bg-stone-900 text-white" : "text-stone-600 hover:bg-stone-100"}`}
                            data-testid={`together-view-${v.key}`}>
                        {v.label}
                    </button>
                ))}
            </div>

            {together && (
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6" data-testid="together-modules">
                    {Object.entries(together.modules).map(([mod, data]) => (
                        <div key={mod} className="bg-white border border-stone-200 rounded-xl p-3" data-testid={`together-mod-${mod}`}>
                            <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold truncate">{mod}</div>
                            <div className="text-2xl font-display tabular-nums text-stone-900 mt-1">{data.count}</div>
                        </div>
                    ))}
                </div>
            )}

            {/* 6. Household VAULT Brief */}
            {brief && (
                <div className="bg-stone-900 text-white rounded-2xl p-5 mb-6" data-testid="household-brief">
                    <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold text-amber-400 mb-1">
                        Household VAULT Brief
                    </div>
                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                        {(brief.items || []).map(it => (
                            <div key={it.kind} className="border border-white/10 rounded-lg p-3">
                                <div className="text-[10.5px] uppercase tracking-widest text-stone-400 font-semibold">{it.kind}</div>
                                <div className="text-2xl font-display tabular-nums mt-1">{it.count}</div>
                            </div>
                        ))}
                    </div>
                    <div className="text-[11px] text-stone-400 mt-3">{brief.note}</div>
                </div>
            )}

            {/* 7. Members */}
            <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden mb-6" data-testid="household-members">
                <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
                    <Users size={13} className="text-stone-500" />
                    <h2 className="text-lg font-display text-stone-900">Household members</h2>
                </div>
                {(state.members || []).map(m => (
                    <div key={m.id} className="border-b border-stone-100 last:border-0 px-4 py-3 flex items-center justify-between gap-2 flex-wrap"
                         data-testid={`member-${m.id}`}>
                        <div className="min-w-0 flex-1">
                            <div className="text-[13.5px] font-semibold text-stone-900">
                                {m.name || m.email}
                                <span className="ml-2 text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-stone-100 text-stone-700">{m.role}</span>
                                <span className={`ml-1 text-[10.5px] font-semibold px-2 py-0.5 rounded-full ${m.status === "Active" ? "bg-emerald-50 text-emerald-800" : "bg-stone-100 text-stone-500"}`}>{m.status}</span>
                            </div>
                            <div className="text-[11.5px] text-stone-500">
                                {m.email} · {m.relationship || "—"} · joined {(m.joined_at || "").slice(0, 10) || "—"}
                            </div>
                        </div>
                        <div className="flex gap-1 shrink-0">
                            {m.role !== "Owner" && (
                                <>
                                    <select value={m.role} onChange={e => changeRole(m, e.target.value)}
                                            className="text-[11.5px] border border-stone-300 rounded px-2 py-1"
                                            data-testid={`member-role-${m.id}`}>
                                        {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    <button onClick={() => previewDeparture(m)}
                                            className="text-[11.5px] font-semibold text-stone-700 hover:text-stone-900 px-2 py-1 rounded border border-stone-200 inline-flex items-center gap-1"
                                            data-testid={`member-preview-${m.id}`}>
                                        <LogOut size={10} /> If they leave
                                    </button>
                                    <button onClick={() => removeMember(m)}
                                            className="text-[11.5px] font-semibold text-rose-700 hover:text-rose-900 px-2 py-1 rounded border border-rose-200 inline-flex items-center gap-1"
                                            data-testid={`member-remove-${m.id}`}>
                                        <Trash2 size={10} /> Remove
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* Departure preview modal */}
            {departure && (
                <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4" onClick={() => setDeparture(null)} data-testid="departure-preview-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-3">
                            <LogOut size={13} className="text-stone-500" />
                            <h3 className="text-lg font-display text-stone-900">If {departure.member?.name} leaves</h3>
                        </div>
                        <div className="text-[12px] text-stone-500 mb-3">
                            Here&rsquo;s what happens to each record class. VAULT never silently transfers private
                            records or documents to another member.
                        </div>
                        <div className="space-y-2 max-h-72 overflow-y-auto">
                            {Object.entries(departure.counts || {}).map(([mod, c]) => (
                                <div key={mod} className="border border-stone-200 rounded-lg p-2.5">
                                    <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">{mod}</div>
                                    <div className="text-[12px] text-stone-700 mt-1">
                                        Personal: <span className="tabular-nums font-semibold">{c.personal}</span> ·
                                        Joint: <span className="tabular-nums font-semibold">{c.joint}</span> ·
                                        Household: <span className="tabular-nums font-semibold">{c.household}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-3">{departure.note}</div>
                        <div className="text-right mt-3">
                            <button onClick={() => setDeparture(null)}
                                    className="text-[12px] font-semibold text-stone-700 hover:text-stone-900 border border-stone-300 rounded-full px-3.5 py-1.5"
                                    data-testid="departure-close">Close</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 8. Invite form */}
            <div className="bg-white border border-stone-200 rounded-2xl p-4 mb-6" data-testid="invite-form">
                <div className="flex items-center gap-2 mb-2">
                    <UserPlus size={13} className="text-stone-500" />
                    <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">Invite member</div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                    <input value={inv.name} onChange={e => setInv({...inv, name: e.target.value})}
                           placeholder="Name"
                           className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="invite-name" />
                    <input type="email" value={inv.email} onChange={e => setInv({...inv, email: e.target.value})}
                           placeholder="Email"
                           className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="invite-email" />
                    <select value={inv.role} onChange={e => setInv({...inv, role: e.target.value})}
                            className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="invite-role">
                        {ROLES.filter(r => r !== "Owner").map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <select value={inv.relationship} onChange={e => setInv({...inv, relationship: e.target.value})}
                            className="border border-stone-300 rounded-lg px-2 py-1.5 text-[13px]" data-testid="invite-relationship">
                        {RELATIONSHIPS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                </div>
                <div className="flex items-center justify-between mt-2 flex-wrap gap-2">
                    <div className="text-[11px] text-stone-500 flex items-start gap-1">
                        <Info size={11} className="mt-0.5" />
                        This person will only see records you have chosen to share. Everyone must
                        create their own VAULT login — passwords are never shared.
                    </div>
                    <button onClick={openConfirmInvite}
                            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                            data-testid="invite-send">
                        <UserPlus size={11} /> Review &amp; send invite
                    </button>
                </div>
                {lastInvite && (
                    <div className="mt-3 border-t border-stone-100 pt-3" data-testid="invite-link">
                        <div className="text-[11px] uppercase tracking-widest text-stone-500 font-semibold">Share this secure link</div>
                        <div className="flex items-center gap-2 mt-1">
                            <code className="flex-1 min-w-0 text-[11px] font-mono bg-stone-100 rounded px-2 py-1 truncate">
                                {`${window.location.origin}/accept-invite/${lastInvite.token}`}
                            </code>
                            <button onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/accept-invite/${lastInvite.token}`); toast.success("Copied"); }}
                                    className="text-[11px] font-semibold text-stone-700 hover:text-stone-900 inline-flex items-center gap-1 px-2 py-1 rounded border border-stone-200">
                                <Copy size={10} /> Copy
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Invite confirm modal — final privacy review */}
            {confirmInvite && review && (
                <div className="fixed inset-0 z-50 bg-stone-900/60 flex items-center justify-center p-4" onClick={() => setConfirmInvite(false)} data-testid="invite-confirm-modal">
                    <div className="bg-white rounded-2xl max-w-lg w-full p-5" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-2 mb-2">
                            <Shield size={13} className="text-stone-500" />
                            <h3 className="text-lg font-display text-stone-900">Ready to invite {inv.name}?</h3>
                        </div>
                        <div className="text-[12.5px] text-stone-700">
                            They will get their own login. Your current privacy preset is
                            <strong> {preset.current}</strong>. Here&rsquo;s a summary of what will be visible
                            once they accept:
                        </div>
                        <div className="mt-3 border border-stone-200 rounded-lg p-3 space-y-1 max-h-56 overflow-y-auto">
                            {Object.entries(review).map(([mod, buckets]) => (
                                <div key={mod} className="text-[12px] text-stone-700 flex items-center justify-between gap-2">
                                    <span className="capitalize">{mod}</span>
                                    <span className="text-stone-500">
                                        Private <strong>{buckets.Private}</strong> · Summary <strong>{buckets["Shared Summary"]}</strong> · Full <strong>{buckets["Shared Full"]}</strong>
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-3">
                            Records marked Private (and Unclassified) will never appear in their household view.
                            You can change any label from the record itself at any time.
                        </div>
                        <div className="flex items-center justify-end gap-2 mt-4">
                            <button onClick={() => setConfirmInvite(false)}
                                    className="text-[12px] font-semibold text-stone-700 hover:text-stone-900 border border-stone-300 rounded-full px-3.5 py-1.5"
                                    data-testid="invite-cancel">Cancel</button>
                            <button onClick={sendInvite}
                                    className="text-[12px] font-semibold text-white bg-stone-900 hover:bg-stone-700 rounded-full px-3.5 py-1.5"
                                    data-testid="invite-confirm">Send invite</button>
                        </div>
                    </div>
                </div>
            )}

            {/* 9. Family Demo Seed */}
            <div className="border border-dashed border-amber-300 bg-amber-50/40 rounded-2xl p-4 mb-6" data-testid="family-demo-card">
                <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={13} className="text-amber-700" />
                    <div className="text-[10.5px] uppercase tracking-widest text-amber-800 font-semibold">
                        Family Demo · DEMO DATA ONLY
                    </div>
                </div>
                <p className="text-[12px] text-stone-600 mb-3">
                    Populate a synthetic Alex &amp; Sam household with ~10 sample records across ownership
                    (Personal / Joint / Household) and visibility (Private / Shared Summary / Shared Full).
                    Every seeded row is tagged <code>_demo=True</code> so it can be purged in one click.
                    Your real records are never touched.
                </p>
                <div className="flex flex-wrap gap-2">
                    <button onClick={seedDemo} disabled={busySeed}
                            className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full bg-stone-900 text-white text-[12px] font-semibold hover:bg-stone-700 disabled:opacity-50"
                            data-testid="seed-family-demo">
                        <Sparkles size={11} /> {busySeed ? "Working…" : "Seed Alex + Sam"}
                    </button>
                    <button onClick={purgeDemo} disabled={busySeed}
                            className="inline-flex items-center gap-1 px-3.5 py-1.5 rounded-full border border-stone-300 text-stone-700 text-[12px] font-semibold hover:border-stone-500 disabled:opacity-50"
                            data-testid="purge-family-demo">
                        <Trash2 size={11} /> Remove demo rows
                    </button>
                </div>
            </div>

            <div className="mt-6 flex items-start gap-2 text-[11px] text-stone-500 max-w-3xl">
                <Shield size={12} className="mt-0.5 shrink-0" />
                Server-side enforcement: Private records never appear in Together / Partner / Joint / Household
                views. VAULT never transfers money between members — shared-expense metadata is organisational only.
            </div>
        </div>
    );
}
